/**
 * L'attuazione di `FocusPort` per browser e Android TV.
 *
 * **È l'unico posto dell'applicazione che chiama `addEventListener('keydown')`.** Nessuna
 * schermata lo fa, e non deve poterlo fare: è la terza giunzione (01-ARCHITETTURA §2.4).
 */

import type { FocusPort, RemoteKey, RemotePlatform } from '@player/ports';
import type { Rect } from '@player/domain';
import { keyMapFor, translateKey, type KeyMap } from './keyMaps.js';

export { keyMapFor, translateKey, filledPlatforms, type KeyMap } from './keyMaps.js';

/**
 * Riconosce la piattaforma dallo User-Agent.
 *
 * Distinzione che sembra pedante e non lo è: un box AOSP non certificato è Android ma non
 * è Android TV, e il caso peggiore è lui (01-ARCHITETTURA §2.5). Per i codici tasto le due
 * cose coincidono — è la stessa tabella — quindi qui basta separare Android dal resto.
 */
export function detectPlatform(userAgent: string): RemotePlatform {
  const agent = userAgent.toLowerCase();
  if (agent.includes('tizen')) return 'tizen';
  if (agent.includes('web0s') || agent.includes('webos')) return 'webos';
  if (agent.includes('android')) return 'androidTv';
  return 'browser';
}

export class KeyboardFocusAdapter implements FocusPort {
  readonly platform: RemotePlatform;
  private readonly map: KeyMap;
  private readonly listeners = new Set<(key: RemoteKey) => boolean>();
  private detach: (() => void) | null = null;

  constructor(private readonly target: EventTarget, platform: RemotePlatform) {
    this.platform = platform;
    this.map = keyMapFor(platform);
  }

  subscribe(listener: (key: RemoteKey) => boolean): () => void {
    this.listeners.add(listener);
    this.ensureAttached();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) this.teardown();
    };
  }

  private ensureAttached(): void {
    if (this.detach) return;
    const handler = (event: Event): void => {
      const keyboardEvent = event as KeyboardEvent;
      const key = translateKey(this.map, keyboardEvent);
      if (!key) return;
      if (this.dispatch(key)) keyboardEvent.preventDefault();
    };
    this.target.addEventListener('keydown', handler);
    this.detach = () => this.target.removeEventListener('keydown', handler);
  }

  private teardown(): void {
    this.detach?.();
    this.detach = null;
  }

  /**
   * L'ultimo iscritto ha la precedenza: un modale cattura il fuoco, e con la richiesta
   * del PIN o la conferma di uscita aperte le frecce si muovono solo dentro il modale
   * (03-SCHERMATE-TV §1).
   */
  private dispatch(key: RemoteKey): boolean {
    const ordered = [...this.listeners].reverse();
    for (const listener of ordered) {
      if (listener(key)) return true;
    }
    return false;
  }

  measure(element: unknown): Rect | null {
    if (element === null || typeof element !== 'object') return null;
    const candidate = element as { getBoundingClientRect?: () => DOMRect };
    if (typeof candidate.getBoundingClientRect !== 'function') return null;
    const rect = candidate.getBoundingClientRect();
    return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
  }

  injectForMeasurement(key: RemoteKey): void {
    this.dispatch(key);
  }

  destroy(): void {
    this.teardown();
    this.listeners.clear();
  }
}
